import React, { useMemo } from "react";
import { comparatorBy } from "diagnostics-lib/comparator";
import type { Output } from "./call";
import call from "./call";
import useQuery, { useGetQueryData, useMutateBody } from "./useQuery";
import { useChannelUpdates } from "./ws/useChannelUpdates";
import { indexByTwoFunctions } from "diagnostics-lib/iterable";
const comparator = comparatorBy("createdAt");
export const NONE_LABEL_FAILURE_STATUS = "TO_BE_ANALYSED";
export type FailureValueStatus =
  | Output<"GET", "/projects/:projectId(uuid)/failure-metadata">["body"][number]["status"]
  | "TO_BE_ANALYSED";
export const useFailureMetadata = (projectId: string) => {
  const getCachedItems = useGetQueryData("/projects/:projectId(uuid)/failure-metadata", {
    routeParams: { projectId },
  });

  const fetchUpdatedItems = React.useCallback(
    async (since: string) => {
      const { failureMetadata, deletedFailureMetadataIds } = (
        await call("GET", "/projects/:projectId(uuid)/failure-metadata/updates/:since(date)", {
          routeParams: { projectId, since: new Date(since) },
        })
      ).body;

      return [failureMetadata, deletedFailureMetadataIds] as const;
    },
    [projectId],
  );

  useChannelUpdates(
    { key: "failure_metadata", projectId },
    {
      readItems: React.useCallback(() => getCachedItems()?.body, [getCachedItems]),
      fetchUpdatedItems,
      mutateItems: useMutateBody("/projects/:projectId(uuid)/failure-metadata", {
        routeParams: { projectId },
      }),
    },
  );

  const failureMetadata = useQuery("/projects/:projectId(uuid)/failure-metadata", {
    routeParams: {
      projectId,
    },
  }).body;

  return React.useMemo(() => failureMetadata.sort(comparator), [failureMetadata]);
};

export type RawFailureValueStatus = ReturnType<typeof useFailureMetadata>;

export const useIndexedFailureMetadata = (projectId: string) => {
  const failureValueStatus = useFailureMetadata(projectId);

  const indexed = useMemo(
    () =>
      indexByTwoFunctions(
        failureValueStatus,
        (fvs: { date: number }) => fvs.date,
        fvs => fvs.failureName,
      ),
    [failureValueStatus],
  );

  const getFailureStatus = React.useCallback(
    (date: number, failureName: string) =>
      indexed.get(date)?.get(failureName)?.status ?? NONE_LABEL_FAILURE_STATUS,
    [indexed],
  );

  const getFailureReparabilty = React.useCallback(
    (date: number, failureName: string) =>
      indexed.get(date)?.get(failureName)?.reparability ?? null,
    [indexed],
  );

  const getDateMetadata = React.useCallback((date: number) => indexed.get(date), [indexed]);

  return React.useMemo(
    () => ({ failureValueStatus, getFailureStatus, getFailureReparabilty, getDateMetadata }),
    [failureValueStatus, getFailureStatus, getFailureReparabilty, getDateMetadata],
  );
};

export type IndexedFailureMetadata = ReturnType<typeof useIndexedFailureMetadata>;
